import { useCallback, useContext, useEffect } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useLocation } from 'react-router-dom';
import useWS from '../components/schedule/hooks/useWS';
import { MeContext } from '../contexts/me.context';
import messagingService from '../services/messaging.service';
import { UnreadStatusResponse } from '../types/messaging.types';

// standalone hook to mark messages as read without subscribing to WebSocket
export const useMarkMessagesAsRead = () => {
  const { me, selectedLocationFull } = useContext(MeContext);
  const clinicId = me?.selectedClinic?.ID;
  const locationId = selectedLocationFull?.ID as number | undefined;
  const queryClient = useQueryClient();

  return useCallback(() => {
    if (!clinicId || !locationId) return;
    queryClient.setQueryData<UnreadStatusResponse | undefined>(
      ['unreadMessages', clinicId, locationId],
      old => ({
        hasUnread: false,
        latestMessageTimestamp: old?.latestMessageTimestamp,
        unreadCount: 0,
      })
    );
  }, [clinicId, locationId, queryClient]);
};

export const useUnreadMessages = () => {
  const { me, selectedLocationFull } = useContext(MeContext);
  const clinicId = me?.selectedClinic?.ID;
  const locationId = selectedLocationFull?.ID as number | undefined;
  const location = useLocation();
  const queryClient = useQueryClient();

  const isOnMessagingPage = location.pathname.startsWith('/messaging');

  // Initial fetch to get the current unread status (one-time only)
  const { data } = useQuery<UnreadStatusResponse>(
    ['unreadMessages', clinicId, locationId],
    () => messagingService.getUnreadStatus(clinicId || -1, undefined, locationId),
    {
      enabled: !!clinicId && !!locationId,
      // No polling - we rely on WebSocket for updates
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    }
  );

  // Handle WebSocket messages for unread status updates
  const handleWebSocketMessage = useCallback(
    (wsData: any) => {
      if (!clinicId || !locationId) return;

      const isMessagingEvent =
        wsData.type === 'newMessage' || wsData.type === 'createMessage' || Boolean(wsData.messages);

      // Process all inbound messages regardless of page location
      // The badge should update even if user is on messaging page (different conversation)
      if (isMessagingEvent) {
        // Only increment for inbound messages (from patient)
        const isInboundMessage =
          wsData.data?.direction === 'inbound' || wsData.direction === 'inbound';

        if (isInboundMessage) {
          // Increment unread count locally for this location
          queryClient.setQueryData<UnreadStatusResponse | undefined>(
            ['unreadMessages', clinicId, locationId],
            old => {
              const current = old?.unreadCount ?? 0;
              const next = current + 1;

              return {
                hasUnread: next > 0,
                latestMessageTimestamp: wsData.data?.sentAt || old?.latestMessageTimestamp,
                unreadCount: next,
              };
            }
          );
        }
      }
    },
    [clinicId, locationId, queryClient]
  );

  // Subscribe to WebSocket updates for unread messages
  useWS(
    clinicId && locationId ? `messaging:${clinicId}:${locationId}` : null,
    handleWebSocketMessage
  );

  const markAsRead = useCallback(() => {
    if (!clinicId || !locationId) return;
    queryClient.setQueryData<UnreadStatusResponse | undefined>(
      ['unreadMessages', clinicId, locationId],
      old => ({
        hasUnread: false,
        latestMessageTimestamp: old?.latestMessageTimestamp,
        unreadCount: 0,
      })
    );
  }, [clinicId, locationId, queryClient]);

  // Mark as read when navigating to messaging page
  useEffect(() => {
    if (isOnMessagingPage) {
      markAsRead();
    }
  }, [isOnMessagingPage, markAsRead]);

  // when on messaging route, always show 0 unread (aka, hide 'unread messages badge')
  // on any other route, show actual unread count
  const actualUnreadCount = isOnMessagingPage ? 0 : (data?.unreadCount ?? 0);

  return {
    hasUnreadMessages: actualUnreadCount > 0,
    unreadCount: actualUnreadCount,
    markAsRead,
  };
};
